import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/api";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart } = useStore();
  const navigate = useNavigate();
  const subtotal = cart.reduce((a, i) => a + i.price * i.qty, 0);

  return (
    <AnimatePresence>
      {cartOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
          <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#FBF7F1] border-l border-[#7E1E3F]/20 flex flex-col"
            data-testid="cart-drawer">
            <div className="flex items-center justify-between p-6 border-b border-[#7E1E3F]/10">
              <h3 className="font-serif-lux text-2xl text-[#2E1A22]">Your Cart</h3>
              <button onClick={() => setCartOpen(false)} className="text-[#857078] hover:text-[#2E1A22]" data-testid="cart-close"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {cart.length === 0 && (
                <div className="text-center pt-16" data-testid="cart-empty">
                  <p className="font-serif-lux text-xl text-[#857078]">Your cart is empty</p>
                  <p className="text-sm text-[#857078] mt-2">A memorable gift is one click away.</p>
                  <button onClick={() => { setCartOpen(false); navigate("/gifts"); }}
                    className="mt-6 text-xs tracking-[0.15em] uppercase font-semibold bg-[#7E1E3F] text-[#FFFFFF] rounded-full px-6 py-3 hover:bg-[#5C1230] transition-colors"
                    data-testid="cart-explore-button">Explore Gifts</button>
                </div>
              )}
              {cart.map((item) => (
                <div key={item.key || item.slug} className="flex gap-4 card-surface rounded-xl p-3" data-testid={`cart-item-${item.key || item.slug}`}>
                  <img src={item.image} alt={item.name} className="w-20 h-24 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-lux text-[#2E1A22] leading-snug line-clamp-2">{item.name}</p>
                    {item.personalization && (
                      <p className="text-[11px] text-[#B4853B] mt-0.5 flex items-center gap-1.5" data-testid={`cart-pers-${item.key}`}>
                        {item.personalization.photo && <img src={item.personalization.photo} alt="" className="w-5 h-5 rounded object-cover" />}
                        Personalized for “{item.personalization.name}”
                      </p>
                    )}
                    <p className="text-[#7E1E3F] text-sm mt-1">{inr(item.price)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => updateQty(item.key || item.slug, item.qty - 1)} className="p-1 border border-[#7E1E3F]/30 rounded-full text-[#857078]" data-testid={`qty-minus-${item.key || item.slug}`}><Minus size={12} /></button>
                      <span className="text-sm text-[#2E1A22] w-4 text-center">{item.qty}</span>
                      <button onClick={() => updateQty(item.key || item.slug, item.qty + 1)} className="p-1 border border-[#7E1E3F]/30 rounded-full text-[#857078]" data-testid={`qty-plus-${item.key || item.slug}`}><Plus size={12} /></button>
                      <button onClick={() => removeFromCart(item.key || item.slug)} className="ml-auto text-[#857078] hover:text-[#E63946]" data-testid={`remove-${item.key || item.slug}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <div className="p-6 border-t border-[#7E1E3F]/10">
                <div className="flex justify-between text-sm text-[#857078] mb-1">
                  <span>Subtotal</span><span data-testid="cart-subtotal" className="text-[#2E1A22]">{inr(subtotal)}</span>
                </div>
                <p className="text-xs text-[#857078] mb-4">Gift wrapping & coupons applied at checkout.</p>
                <button onClick={() => { setCartOpen(false); navigate("/checkout"); }}
                  className="w-full flex items-center justify-center gap-2 bg-[#7E1E3F] text-[#FFFFFF] font-semibold text-sm tracking-wider uppercase rounded-full py-3.5 hover:bg-[#5C1230] transition-colors active:scale-[0.99]"
                  data-testid="cart-checkout-button">
                  Proceed to Checkout <ArrowRight size={16} />
                </button>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
